import { transformUtil } from '@eventbrite/transformation-utils';
import { Schema } from 'normalizr';
import { BUYER_ID } from '../../models/survey';
import { OrderResponse, OrderResponseFormatted } from '../models/order';
import {
    CannedQuestionName,
    InteractiveQuestion,
    QuestionType,
} from '../models/survey';

const orderSchema = new Schema('order');

const getTransformedOrderFromOrderResponse = (data: OrderResponse) => {
    const transformedOrder = transformUtil({
        response: data,
        schema: orderSchema,
    });

    if (!transformedOrder) {
        return null;
    }

    const orderDetails = transformedOrder.entities.order[
        transformedOrder.result
    ] as OrderResponseFormatted;

    if (orderDetails.attendees) {
        orderDetails.attendees.forEach((attendee) => {
            const childQuestions = attendee.survey?.filter(
                (question: InteractiveQuestion) => question.trigger,
            );
            if (childQuestions) {
                for (const childQuestion of childQuestions) {
                    const parentQuestion = attendee.survey?.find(
                        (question: InteractiveQuestion) =>
                            childQuestion.trigger &&
                            childQuestion.trigger.parentQuestionId ==
                                question.questionId,
                    );

                    if (parentQuestion) {
                        if (parentQuestion.childrenQuestions == null) {
                            parentQuestion.childrenQuestions = [];
                        }
                        parentQuestion.childrenQuestions.push(
                            childQuestion.questionId,
                        );
                    }
                }
            }
        });
    }

    return orderDetails;
};

export const getInteractiveQuestionsByAttendeeIdFromOrderResponse = (
    data: OrderResponse,
) => {
    const order = getTransformedOrderFromOrderResponse(data);

    if (!order) {
        return {};
    }

    // qstZipCode component will also set TaxRegistrationId, so we can filter out this question
    if (
        order.survey &&
        order.survey.find(
            (question) => question.questionId === CannedQuestionName.qstZipCode,
        ) != null
    ) {
        order.survey = order.survey.filter(
            (question) =>
                question.questionId != CannedQuestionName.taxRegistrationId,
        );
    }

    const baseQuestions: Record<string, Array<InteractiveQuestion>> = {
        [BUYER_ID]: order.survey,
    };

    const attendees = order.attendees ?? [];

    return attendees.reduce(
        (otherAttendees, attendee) => ({
            ...otherAttendees,
            [attendee.id]: attendee.survey,
        }),
        baseQuestions,
    );
};

export const getQuestionsCollectionFromOrderResponse = (
    data: OrderResponse,
) => {
    const order = getTransformedOrderFromOrderResponse(data);

    return order ? order.questions : null;
};

type InputFieldValue = string;
type DateFieldValue = { day: number; month: number; year: number };
type AddressFieldValue = Record<string, string>;
type WaiverFieldValue = string;
type ChoiceFieldValue = Array<{ id: string }>;

const getDateFieldPrefilledValue = (
    surveyFieldValue: DateFieldValue,
): {
    day: string;
    month: string;
    year: string;
} => {
    return {
        day: surveyFieldValue.day.toString(),
        month: surveyFieldValue.month.toString(),
        year: surveyFieldValue.year.toString(),
    };
};

const getAddressFieldPrefilledValue = (
    surveyFieldValue: AddressFieldValue,
): Record<string, string> =>
    Object.keys(surveyFieldValue).reduce(
        (accum: Record<string, string>, key: string) => ({
            ...accum,
            [key
                .replace(/^address_/, 'address')
                .replace(/^postal_code/, 'postal')]: surveyFieldValue[key],
        }),
        {},
    );

const getWaiverFieldPrefilledValue = (
    surveyFieldValue: WaiverFieldValue,
): boolean => surveyFieldValue === 'accepted';

const getSingleChoiceDropdownFieldPrefilledValue = (
    surveyFieldValue: ChoiceFieldValue,
): string => surveyFieldValue[0].id;

const getSingleChoiceRadioFieldPrefilledValue = (
    surveyFieldValue: ChoiceFieldValue,
): string =>
    // Custom and Canned radio survey responses have different format
    surveyFieldValue[0].id || surveyFieldValue.toString();

const getMultipleChoiceFieldPrefilledValue = (
    surveyFieldValue: ChoiceFieldValue,
): Array<string> =>
    surveyFieldValue.map((surveyFieldValueItem) => surveyFieldValueItem.id);

export const getFieldPrefilledValue = (
    params:
        | {
              value: DateFieldValue;
              type: QuestionType.date;
          }
        | {
              value: AddressFieldValue;
              type: QuestionType.address;
          }
        | {
              value: WaiverFieldValue;
              type: QuestionType.waiver;
          }
        | {
              value: ChoiceFieldValue;
              type:
                  | QuestionType.radio
                  | QuestionType.dropdown
                  | QuestionType.checkbox;
          }
        | {
              value: InputFieldValue;
              type:
                  | QuestionType.email
                  | QuestionType.number
                  | QuestionType.paragraph
                  | QuestionType.phone
                  | QuestionType.text
                  | QuestionType.url;
          },
) => {
    switch (params.type) {
        case QuestionType.date:
            return getDateFieldPrefilledValue(params.value);
        case QuestionType.address:
            return getAddressFieldPrefilledValue(params.value);
        case QuestionType.waiver:
            return getWaiverFieldPrefilledValue(params.value);
        case QuestionType.radio:
            return getSingleChoiceRadioFieldPrefilledValue(params.value);
        case QuestionType.dropdown:
            return getSingleChoiceDropdownFieldPrefilledValue(params.value);
        case QuestionType.checkbox:
            return getMultipleChoiceFieldPrefilledValue(params.value);
        case QuestionType.email:
        case QuestionType.number:
        case QuestionType.paragraph:
        case QuestionType.phone:
        case QuestionType.text:
        case QuestionType.url:
            return params.value;
    }
};
